import React, { useEffect, useState } from 'react';
import '../modalstyles/CompanyDocumentModal.css';

const CATEGORY_OPTIONS = [
  'Loan Sanction',
  'Loan Application',
  'Loan Promise',
  'Loan Card',
  'RD Certificate',
  'DD Certificate',
];

const TYPE_OPTIONS = ['Signature', 'Stamp'];

const initialFormState = {
  id: null,
  category: '',
  type: '',
  fileName: '',
  fileData: null,
};

const CompanyDocumentModal = ({ onClose }) => {
  const [documents, setDocuments] = useState([]);
  const [addForm, setAddForm] = useState(initialFormState);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState(initialFormState);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 5;

  useEffect(() => {
    const saved = window.localStorage.getItem('companyDocuments');
    if (saved) {
      try {
        setDocuments(JSON.parse(saved));
      } catch (err) {
        console.warn('Failed to parse saved company documents', err);
      }
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem('companyDocuments', JSON.stringify(documents));
  }, [documents]);

  const filteredDocuments = documents.filter((doc) => {
    const value = `${doc.category} ${doc.type} ${doc.fileName}`.toLowerCase();
    return value.includes(search.toLowerCase());
  });

  const totalPages = Math.max(1, Math.ceil(filteredDocuments.length / ITEMS_PER_PAGE));
  const paginatedDocuments = filteredDocuments.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAddForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setAddForm((prev) => ({ ...prev, fileName: '', fileData: null }));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setAddForm((prev) => ({
        ...prev,
        fileName: file.name,
        fileData: reader.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const clearForm = () => {
    setAddForm(initialFormState);
    setCurrentPage(1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!addForm.category || !addForm.type) {
      alert('Please select both category and type.');
      return;
    }

    if (!addForm.fileData) {
      alert('Please upload a JPG or PNG file.');
      return;
    }

    setDocuments((prev) => [
      ...prev,
      {
        id: Date.now(),
        category: addForm.category,
        type: addForm.type,
        fileName: addForm.fileName,
        fileData: addForm.fileData,
      },
    ]);
    alert('Document added successfully.');
    clearForm();
  };

  const handleEdit = (doc) => {
    setEditForm({
      id: doc.id,
      category: doc.category,
      type: doc.type,
      fileName: doc.fileName,
      fileData: doc.fileData,
    });
    setEditModalOpen(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setEditForm((prev) => ({ ...prev, fileName: '', fileData: null }));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setEditForm((prev) => ({
        ...prev,
        fileName: file.name,
        fileData: reader.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleUpdateSubmit = (e) => {
    e.preventDefault();

    if (!editForm.category || !editForm.type) {
      alert('Please select both category and type.');
      return;
    }

    if (!editForm.fileData) {
      alert('Please upload a JPG or PNG file.');
      return;
    }

    setDocuments((prev) =>
      prev.map((doc) =>
        doc.id === editForm.id
          ? {
              ...doc,
              category: editForm.category,
              type: editForm.type,
              fileName: editForm.fileName,
              fileData: editForm.fileData,
            }
          : doc
      )
    );

    setEditModalOpen(false);
    setEditForm(initialFormState);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditForm(initialFormState);
  };

  const handleDelete = (id) => {
    if (!window.confirm('Delete this document?')) return;
    setDocuments((prev) => prev.filter((doc) => doc.id !== id));
  };

  return (
    <div className="CompanyDocumentModal-backdrop">
      <div className="CompanyDocumentModal-modal">
        <div className="CompanyDocumentModal-header">
          <div>
            <h3>Company Document</h3>
            <p>Add or update company document uploads.</p>
          </div>
          <button className="CompanyDocumentModal-closeBtn" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="CompanyDocumentModal-body">
          <form className="CompanyDocumentModal-form" onSubmit={handleSubmit}>
            <label>Loan Agreement</label>
            <select name="category" value={addForm.category} onChange={handleInputChange}>
              <option value="">Select a category</option>
              {CATEGORY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <label>Type</label>
            <select name="type" value={addForm.type} onChange={handleInputChange}>
              <option value="">Select type</option>
              {TYPE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            {addForm.type && (
              <>
                <label>Upload File (JPG, PNG)</label>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={handleFileChange}
                />
              </>
            )}

            {addForm.fileData && (
              <div className="CompanyDocumentModal-previewWrapper">
                <img
                  src={addForm.fileData}
                  alt="preview"
                  className="CompanyDocumentModal-preview"
                />
                <span>{addForm.fileName}</span>
              </div>
            )}

            <div className="CompanyDocumentModal-formButtons">
              <button type="submit" className="CompanyDocumentModal-saveBtn">
                Add Document
              </button>
              <button type="button" className="CompanyDocumentModal-clearBtn" onClick={clearForm}>
                Clear
              </button>
            </div>
          </form>

          <div className="CompanyDocumentModal-tableSection">
            <div className="CompanyDocumentModal-tableHeader">
              <h4>Saved Documents</h4>
              <input
                type="text"
                placeholder="Search documents..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="CompanyDocumentModal-tableWrapper">
              <table>
                <thead>
                  <tr>
                    <th>SN</th>
                    <th>Category</th>
                    <th>Type</th>
                    <th>File</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDocuments.length > 0 ? (
                    paginatedDocuments.map((doc, index) => (
                      <tr key={doc.id}>
                        <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                        <td>{doc.category}</td>
                        <td>{doc.type}</td>
                        <td>
                          <div className="CompanyDocumentModal-rowPreview">
                            <img src={doc.fileData} alt={doc.fileName} />
                            <span>{doc.fileName}</span>
                          </div>
                        </td>
                        <td>
                          <button className="CompanyDocumentModal-editBtn" onClick={() => handleEdit(doc)}>
                            Edit
                          </button>
                          <button className="CompanyDocumentModal-deleteBtn" onClick={() => handleDelete(doc.id)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5">No documents available.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="CompanyDocumentModal-pagination">
                <button onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
                  Prev
                </button>
                <span>
                  Page {currentPage} of {totalPages}
                </span>
                <button onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {editModalOpen && (
        <div className="CompanyDocumentModal-editBackdrop" onClick={closeEditModal}>
          <div className="CompanyDocumentModal-editDialog" onClick={(e) => e.stopPropagation()}>
            <div className="CompanyDocumentModal-editHeader">
              <h4>Edit Company Document</h4>
              <button className="CompanyDocumentModal-closeBtn" onClick={closeEditModal}>
                &times;
              </button>
            </div>
            <form className="CompanyDocumentModal-editForm" onSubmit={handleUpdateSubmit}>
              <label>Loan Agreement</label>
              <select name="category" value={editForm.category} onChange={handleEditInputChange}>
                <option value="">Select a category</option>
                {CATEGORY_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>

              <label>Type</label>
              <select name="type" value={editForm.type} onChange={handleEditInputChange}>
                <option value="">Select type</option>
                {TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>

              <label>Upload File (JPG, PNG)</label>
              <input type="file" accept=".jpg,.jpeg,.png" onChange={handleEditFileChange} />

              {editForm.fileData && (
                <div className="CompanyDocumentModal-previewWrapper">
                  <img
                    src={editForm.fileData}
                    alt="preview"
                    className="CompanyDocumentModal-preview"
                  />
                  <span>{editForm.fileName}</span>
                </div>
              )}

              <div className="CompanyDocumentModal-formButtons">
                <button type="submit" className="CompanyDocumentModal-saveBtn">
                  Save Changes
                </button>
                <button type="button" className="CompanyDocumentModal-clearBtn" onClick={closeEditModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDocumentModal;
